import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { ApiResponse } from '../../types';
import { apiClient } from '../client';

export interface FileStatusItem {
  id: string;
  created_by: string;
  size: number;
  real_name: string;
  doc_type: string;
  status: string;
  error: string;
}

export interface FileStatusResponse {
  data: FileStatusItem[];
}

export interface UploadFileInfo {
  fileNameUUI: string;
  size: number;
  datetime: string;
  fileName: string;
  fileType: string;
  docType: string;
}

export interface UploadRequest {
  userInfo: { email: string };
  fileInfo: UploadFileInfo;
  dbName: string;
}

export interface SignedUrlResponse {
  uploadPreSignedURL: string;
}

export const uploadService = {
  // Get signed URL for file upload
  async getSignedUrl(
    fileInfo: UploadRequest
  ): Promise<ApiResponse<SignedUrlResponse>> {
    return apiClient.post('/s3upload', fileInfo);
  },

  // Upload file to S3 using signed URL
  async uploadToS3(signedUrl: string, file: File): Promise<void> {
    await axios.put(signedUrl, file, {
      withCredentials: true,
      headers: {
        'Content-Type': file.type,
      },
    });
  },

  // Process uploaded file
  async processFile(fileInfo: UploadRequest): Promise<ApiResponse> {
    return apiClient.post('/s3operator', fileInfo);
  },

  // Get file processing status
  async getFileStatus(params: {
    datetime: string;
    docType: string;
    dbName: string;
  }): Promise<ApiResponse<FileStatusItem[]>> {
    return apiClient.post('/filestatus', params);
  },

  // Complete file upload process
  async uploadFile(
    file: File,
    docType: string,
    userEmail: string,
    dbName: string
  ): Promise<void> {
    const datetime = new Date().toISOString().slice(0, 10);

    const fileInfo: UploadRequest = {
      userInfo: { email: userEmail },
      fileInfo: {
        fileNameUUI: uuidv4(),
        size: file.size,
        datetime: datetime,
        fileName: file.name,
        fileType: file.type,
        docType: docType,
      },
      dbName: dbName,
    };

    try {
      // Step 1: Get signed URL
      const signedUrlResponse = await this.getSignedUrl(fileInfo);

      // Step 2: Upload file to S3
      await this.uploadToS3(signedUrlResponse.data.uploadPreSignedURL, file);

      // Step 3: Process file
      await this.processFile(fileInfo);
    } catch (error) {
      console.error('Upload failed for file:', file.name, error);
      throw new Error(`Upload failed for ${file.name}`);
    }
  },

  // Batch upload multiple files
  async uploadFiles(
    files: File[],
    docType: string,
    userEmail: string,
    dbName: string
  ): Promise<{ succeeded: string[]; failed: string[] }> {
    const promises = files.map((file) =>
      this.uploadFile(file, docType, userEmail, dbName)
        .then(() => file.name)
        .catch(() => {
          throw file.name;
        })
    );

    const results = await Promise.allSettled(promises);

    const succeeded = results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => (result as PromiseFulfilledResult<string>).value);

    const failed = results
      .filter((result) => result.status === 'rejected')
      .map((result) => (result as PromiseRejectedResult).reason);

    return { succeeded, failed };
  },
};
